/**
 * Static information architecture for the web left menu, derived 1:1
 * from the v37 prototype's `.sidebar-nav`.
 *
 * v37 restructured the sidebar into the recruiting-lifecycle taxonomy
 * from the NU Technology Outline: 8 sections, each carrying a stage icon.
 * The stage numbers v37 prefixed them with were removed (Trello #836): users
 * saw 1, 2, 3, 6, 7, because Screening and Assessing have no pages yet and
 * only appear in preview. All child items sit at the SAME indent level under
 * their section (the v37 nested sub-row was retired on 2026-06-16 — a 2-level
 * visual model didn't scale to N-level future sections).
 *
 * Source: `D&D/meetings/2026-05-19/artifacts/nu_assist_v2_welcome_v37.html`.
 *
 * Modelling rules (per the 2026-06-02 routing + feature-gating spec):
 *   - EVERY item gets a stable `id` (the previously inert v37 tools —
 *     RocketReach → Thrive, AgendaGPT, Administration, etc. — get ids
 *     too) plus a `path` (its router target) and `sectionId` (its parent
 *     section's id, so the access resolver can apply the section+page
 *     hierarchy without walking the tree). (Find on LinkedIn was an
 *     example here too; deleted in PR #305 per Jared feedback.)
 *   - The visibility-dictating `status` field is REMOVED. Visibility is
 *     now derived from the flag registry + preview state (see
 *     `useFeatureAccess`), NOT hardcoded here.
 *   - A small optional `previewBadge?` is kept so Administration can read
 *     `nice to have` IN preview; it overrides only the badge label, never
 *     visibility. Default badge (when omitted) is `coming-soon`.
 *   - Sections keep their v37 `id` (`data-section`) values.
 */
import { activeFlags, isPageReleased, type FeatureFlags } from "../config/featureFlags";
import {
  Trophy,
  Compass,
  Send,
  MonitorPlay,
  ClipboardCheck,
  Users,
  CheckCircle2,
  Settings,
  List,
  Combine,
  GitBranch,
  Sparkles,
  Bookmark,
  Puzzle,
  Search,
  Workflow,
  Presentation,
  CalendarDays,
  Mail,
  LayoutGrid,
  SearchCheck,
  Share2,
  Smartphone,
  Download,
  Shield,
  Building2,
  type LucideIcon,
} from "lucide-react";

/** Optional badge label shown for an off item IN preview only. */
export type PreviewBadge = "coming-soon" | "nice-to-have";

/** @deprecated kept for backward-compat of the public type export. */
export type NavStatus = PreviewBadge;

export interface LeftMenuItem {
  /** Stable page id (matches `featureFlags` page ids). */
  id: string;
  /** Owning section id (matches `featureFlags` section ids). */
  sectionId: string;
  /** Router target. */
  path: string;
  label: string;
  /**
   * Optional short name shown ONLY in the left-nav. When omitted the nav
   * falls back to `label`. Decouples the compact nav wording from the
   * canonical `label`, which is still used for the page title (e.g.
   * `ComingSoonPage` via `routes.tsx`) and everywhere else — so shrinking
   * the nav never shrinks the inner page heading.
   */
  navLabel?: string;
  /**
   * When set, the item is a shortcut to an externally-hosted tool rather than
   * an in-app route: clicking opens this URL in a new tab (the nuadvisory
   * "external-link" migration type — e.g. the AgendaGPT n8n form). The router
   * `path` is still required (used as the gating/active key), but navigation is
   * bypassed in favour of the anchor.
   */
  externalUrl?: string;
  /** Per-item glyph (nearest lucide equivalent of v37's SVG). */
  icon: LucideIcon;
  /**
   * Preview-only badge label override. Omitted → `coming-soon`. The badge
   * is rendered ONLY when previewing an off item; it never affects
   * visibility.
   */
  previewBadge?: PreviewBadge;
  /**
   * Optional self-expiring "new" pill (the `NewBadge` primitive). Shown for
   * a RELEASED item by DEFAULT (independent of preview) — and, since
   * 2026-09-02, for a preview-REVEALED item too — for the
   * `PILL_WINDOW_DAYS` window that starts on `since`, then it disappears.
   * The pill reads a bare "new" (no date stamp — sidebar space is tight).
   * New-badge and preview-badge stay mutually exclusive with the pill as
   * tiebreak: while the window is open a revealed item shows the pill, and
   * the coming-soon balloon resumes when it lapses (see `announcementFor`).
   */
  newBadge?: AnnouncementWindow;
  /**
   * Optional self-expiring "Updated" pill. Identical mechanics to
   * `newBadge` — same `{ since }` window, same `isAnnouncementActive` gate,
   * same `released`-only requirement — but a different word and a different
   * token family, for the case `newBadge` cannot express: a surface the
   * reader ALREADY had, replaced by a new version. Calling that "new" would
   * be wrong (they have used it for weeks); calling it nothing would hide
   * that it changed under them.
   *
   * Mutually exclusive with `newBadge` in practice (an item is either new or
   * updated, not both). `WebLeftMenu` resolves `newBadge` first and only
   * falls through to this, so setting both is a no-op on the second rather
   * than two stacked pills.
   */
  updatedBadge?: AnnouncementWindow;
  /** Permanently muted tone (v37: Administration placeholder). */
  muted?: boolean;
  /**
   * Delivery-phase membership (1..2, per `PHASE_PAGE_IDS` in
   * `useFeatureAccess.ts`). Omitted for items with no defined phase yet —
   * those are either released already, or reachable only via the
   * `?preview=all` radar (`RADAR_ONLY_PAGE_IDS`). Mirrors the existing
   * `previewBadge?`/`newBadge?` optional-field pattern.
   */
  phase?: 1 | 2;
}

export interface LeftMenuSection {
  /** v37 `data-section`. */
  id: string;
  label: string;
  /**
   * Optional short name shown ONLY in the left-nav section header. Falls
   * back to `label` when omitted. Same intent as `LeftMenuItem.navLabel`:
   * the canonical `label` keeps feeding the dashboard grid cards and any
   * other consumer.
   */
  navLabel?: string;
  icon: LucideIcon;
  /** Collapsible only for sections with real pages (v37 behaviour). */
  collapsible: boolean;
  /** Expanded on first render (Research & Sourcing). */
  defaultOpen?: boolean;
  items: LeftMenuItem[];
}

/**
 * A dated announcement window: an item is announced for the
 * `PILL_WINDOW_DAYS` days that start on `since` (a date-only `YYYY-MM-DD`
 * ISO stamp), then goes quiet on its own.
 *
 * Declaring the START rather than the end is the 2026-08-14 change. Windows
 * used to carry a hand-written `until`, which meant every announcement
 * invented its own lifetime — three weeks for the 2026-08 release, three
 * weeks and a day for Chopbook — and nothing stopped the next one from
 * running for a month. With a start date the length lives in ONE constant
 * and applies to every pill and notice on the site.
 */
export interface AnnouncementWindow {
  /** First day the announcement shows, `YYYY-MM-DD`. */
  since: string;
}

/**
 * How long any announcement stays up: one month (30 days), counted
 * inclusively from `since` (a `since` of the 2nd runs through the 1st of the
 * next month and is dark again on the 2nd).
 *
 * 30 days supersedes the 2026-08-14 one-week rule (operator directive,
 * Jose, 2026-09-02 — the production-freeze release): the frozen features
 * keep evolving on dev for a while before they are delivered to prod, so
 * the "what changed" signal has to survive a longer review cycle. The
 * 2026-08-14 concern (a pill that outstays its welcome stops being read)
 * still stands for prod deliveries — revisit the length when the freeze
 * lifts.
 */
export const PILL_WINDOW_DAYS = 30;

/*
 * `ANNOUNCEMENT_MONTH_2026_09_23` (the "new" pill of `my-imports`, `bct`,
 * `ai-review`, `dash-searches`, `dash-detail`) and `BABY_CHOPS_UPDATED_BADGE`
 * (the "Updated" pill of ChopLink) were deleted on 2026-10-20 with the
 * ChopBook / Thrive import release (Jose): the sidebar announces only
 * Combine & Filter and ChopBook (Company Lists joined them on 2026-10-21).
 * Both windows would have closed on 2026-10-22 anyway.
 */

/**
 * The Baby Chops manual-install warning window — the amber "you may already
 * have a hand-installed copy" card on `/research/baby-chops-v2`.
 *
 * A dated announcement rather than a permanent fixture (2026-08-14, operator
 * directive): the population it addresses is whoever installed Baby Chops
 * from the Drive ZIP or Load-Unpacked before the Store listing existed, and
 * that population shrinks to nothing once they have read it. Chopbook dropped
 * its copy of this card outright in the same change, so the Chopbook page is
 * already clean.
 *
 * Its own constant rather than reusing a pill's window: a warning and a pill
 * are different claims, and each keeps its own date. (Historical:
 * it first ran 2026-08-14 → Fri the 21st, deliberately off the shared Monday
 * so a standing safety warning would not blank for three days; then
 * 2026-09-02 → 2026-10-01 for the freeze.) Re-dated below on 2026-09-23, so
 * the current window runs the 30-day `PILL_WINDOW_DAYS` through 2026-10-22.
 */
export const BABY_CHOPS_MANUAL_INSTALL_NOTICE: AnnouncementWindow = {
  // Re-dated 2026-09-23, the day the freeze lifted. Until today prod
  // recruiters WERE the manual-install population (v1 was their page), and
  // they land on v2 for the first time now — so this is the one month where
  // the warning is addressed at exactly the people who need it.
  since: "2026-09-23",
};

/**
 * The Chopbook Tool "New" pill window — shared by the sidebar nav item
 * (`tool-chopbook.newBadge`) and the `ChopbookTool` page header, so the
 * two cannot drift.
 *
 * "New" rather than "Updated": nothing preceded this tool.
 *
 * Re-dated to 2026-10-20, the ChopBook release day (Jose): the page was behind
 * `?preview=4` / `?preview=chop-book` from 2026-08-14, so the month starts the
 * day recruiters can first open it (2026-10-21 left it dark on release day).
 */
export const CHOPBOOK_NEW_BADGE: AnnouncementWindow = {
  since: "2026-10-20",
};

/**
 * The Company Lists "New" pill window. Released with ChopBook on 2026-10-20
 * (it was phase 3 until then); added the day after the release (Jose,
 * 2026-10-21), dated to the release day like ChopBook's. Its own literal for
 * the same no-drift reason as `CHOPBOOK_NEW_BADGE`.
 */
export const COMPANY_LISTS_NEW_BADGE: AnnouncementWindow = {
  since: "2026-10-20",
};

/**
 * The Combine & Filter "New" pill window. Opened 2026-10-05 for previewers
 * (phase 3, `?preview=3`) and RE-DATED to 2026-10-08 with its release on both
 * stages (Jose, 2026-10-07: every page the release shows announces itself from
 * the day after), per the rule above. If the release reaches prod much later
 * than this date, re-date it again to the prod day so the window is not half
 * spent. Its own literal for the same no-drift reason as `CHOPBOOK_NEW_BADGE`.
 */
export const COMBINE_FILTER_NEW_BADGE: AnnouncementWindow = {
  since: "2026-10-08",
};

/**
 * "New" pill window of the Candidates TAB of Search Detail
 * (`search-candidates`), released with Combine & Filter and Bullseye (Jose,
 * 2026-10-07). A tab is not a menu item, so it is announced through
 * `IN_PAGE_ANNOUNCEMENTS` below and read with the same `announcementFor`.
 */
export const CANDIDATES_TAB_NEW_BADGE: AnnouncementWindow = {
  since: "2026-10-08",
};

/** "New" pill window of the Bullseye TAB of Search Detail (`bullseye`). Same
 *  release and day as `CANDIDATES_TAB_NEW_BADGE`, its own literal. */
export const BULLSEYE_TAB_NEW_BADGE: AnnouncementWindow = {
  since: "2026-10-08",
};

/**
 * Announcements of features that are NOT menu items — tabs, rendered inside
 * another page's route — keyed by their page id. `announcementFor` reads these
 * when the id has no menu item, so the one rule (released or revealed, AND
 * inside the window) covers them too. The section id is the one the page's
 * flag resolves under (`IN_PAGE_FEATURE_IDS` in `routing/routes.tsx`).
 */
export const IN_PAGE_ANNOUNCEMENTS: Readonly<
  Record<string, { sectionId: string; newBadge?: AnnouncementWindow }>
> = {
  "search-candidates": { sectionId: "client-engagement", newBadge: CANDIDATES_TAB_NEW_BADGE },
  bullseye: { sectionId: "client-engagement", newBadge: BULLSEYE_TAB_NEW_BADGE },
};

export const LEFT_MENU_SECTIONS: LeftMenuSection[] = [
  {
    id: "winning",
    label: "Winning",
    icon: Trophy,
    collapsible: true,
    items: [
      {
        id: "search-creds",
        sectionId: "winning",
        path: "/winning/search-creds",
        label: "Search Creds & Slide Creator",
        navLabel: "Creds",
        icon: Presentation,
      },
    ],
  },
  {
    id: "linkedin",
    label: "Research & Sourcing",
    navLabel: "Research",
    icon: Compass,
    collapsible: true,
    defaultOpen: true,
    items: [
      {
        id: "my-imports",
        sectionId: "linkedin",
        path: "/research/imports",
        label: "People Lists",
        // `List`, not `Inbox`: the same glyph as the People Lists column on
        // My Searches (Lauren via Jared, 2026-10-07).
        icon: List,
        phase: 1,
        // Released 2026-08-10 (delivery phase 1).
      },
      {
        // Company lists (epic 2026-09-25-chopbook-company-lists): the sibling of
        // People Lists, directly under it as the prototype places it. Replaces
        // the dark "Company Targets" page. Released 2026-10-20 with ChopBook;
        // it was phase 3 until then.
        id: "company-lists",
        sectionId: "linkedin",
        path: "/research/company-lists",
        label: "Company Lists",
        icon: Building2,
        newBadge: COMPANY_LISTS_NEW_BADGE,
      },
      {
        id: "combine-filter",
        sectionId: "linkedin",
        path: "/research/combine-filter",
        label: "Combine & Filter",
        icon: Combine,
        // Released 2026-10-07 on both stages; it was phase 3 until then.
        newBadge: COMBINE_FILTER_NEW_BADGE,
      },
      {
        id: "bct",
        sectionId: "linkedin",
        path: "/research/company-map",
        label: "Company Map",
        icon: GitBranch,
        phase: 2,
        // Released 2026-08-10 (delivery phase 2).
      },
      {
        id: "ai-review",
        sectionId: "linkedin",
        path: "/research/ai-review",
        label: "AI Review",
        icon: Sparkles,
        phase: 2,
        // Released 2026-08-10 (delivery phase 2).
      },
      {
        id: "tool-rocketreach",
        sectionId: "linkedin",
        path: "/research/rocketreach",
        label: "RocketReach → Thrive",
        navLabel: "RocketReach",
        icon: Bookmark,
      },
      // NOTE: `tool-babychops` (Baby Chops v1, `/research/baby-chops`) used to
      // sit here as a peer of the item below. As of 2026-08-10 v2 REPLACES it
      // rather than sitting beside it: two sidebar rows for one Chrome
      // extension, differing only by install mechanism, was an implementation
      // detail leaking into the information architecture. `/research/baby-chops`
      // now redirects to `/research/baby-chops-v2` (`LEGACY_REDIRECTS` in
      // `routes.tsx`) so existing bookmarks land on the live page instead of
      // dead-clicking to /dashboard via the catch-all.
      {
        id: "tool-babychops-v2",
        sectionId: "linkedin",
        // Path and id keep the `-v2` suffix on purpose. The id keys the
        // `featureFlags` registry AND `PHASE_PAGE_IDS[1]`, and the path keys
        // the redirect above plus every generated route; renaming either to
        // shed the suffix would ripple through all three to change something
        // no user can see. The LABEL is what the user reads, and that is now
        // plain "Baby Chops" — a label/id split, recorded so the next reader
        // does not "fix" it. (Same reasoning the `client-engagement` section
        // documents for its own rename.)
        path: "/research/baby-chops-v2",
        label: "ChopLink",
        // Puzzle glyph = browser extension (Baby Chops is a Chrome extension),
        // matching the design prototype — not a literal "baby" pictogram.
        icon: Puzzle,
        phase: 1,
      },
      {
        id: "tool-chopbook",
        sectionId: "linkedin",
        // Sits next to Baby Chops rather than next to Company Targets even
        // though it is the PitchBook-facing tool, because the reader groups by
        // WHAT IT IS (a browser extension you install) not by which vendor
        // site it runs on. Company Targets is a page in this app; Chopbook is
        // a thing you install. Same reasoning that put Baby Chops here.
        path: "/research/chopbook",
        label: "ChopBook",
        // Puzzle glyph = browser extension, matching the Baby Chops row.
        icon: Puzzle,
        // Released 2026-10-20 (the ChopBook release); behind `?preview=4`
        // from 2026-08-14 until then. Its "new" pill runs from the release day.
        newBadge: CHOPBOOK_NEW_BADGE,
      },
    ],
  },
  {
    id: "sequence",
    label: "Outreach",
    icon: Send,
    collapsible: true,
    items: [
      {
        id: "seq-info",
        sectionId: "sequence",
        path: "/outreach/sequences",
        label: "Sequences",
        icon: Workflow,
      },
    ],
  },
  {
    id: "screening",
    label: "Screening & Interviewing",
    icon: MonitorPlay,
    collapsible: false,
    items: [],
  },
  {
    id: "assessing",
    label: "Assessing Candidates",
    icon: ClipboardCheck,
    collapsible: false,
    items: [],
  },
  {
    id: "client-engagement",
    // Renamed from "Client Engagement" on 2026-08-07: the section is about the
    // searches now, and Agenda GPT is the guest. The ID deliberately stays
    // `client-engagement` — it keys the `featureFlags` registry, every
    // `/client-engagement/*` route path and the preview phase ladder, and
    // renaming it would ripple through all three for nothing a user can see.
    // A label/id split, recorded so the next reader does not "fix" it.
    // No dev `navLabel`: it exists to SHORTEN a long label for the rail, and
    // "Searches" is already short. The rail's own `uppercase` class does the
    // casing (`WebLeftMenu`), so a `navLabel: "SEARCHES"` would render
    // identically while baking presentation into the model — and leaking caps
    // into every consumer that has no such class (accessible names, a future
    // breadcrumb). `navLabel ?? label` covers this section.
    //
    // One name on every stage again since 2026-09-23: the freeze's prod-only
    // "Client Engagement" / "Client" pair existed because `dash-searches` was
    // dark there, and it shipped that day.
    label: "Searches",
    icon: Users,
    collapsible: true,
    // Moved here from the Operations section on 2026-08-05 (epic
    // `2026-08-05-my-searches-search-detail`). A recruiter's searches are the
    // substance of client engagement, not an operations dashboard — and
    // Operations was only ever their parking spot while they were inert v37
    // placeholders. Both are Phase 2, so `?preview=2` reveals them together
    // with the Search Detail sub-route they share.
    //
    // They LEAD the section as of 2026-08-07, with Agenda GPT demoted to last.
    // `firstReleasedPageHref` walks this order, but both are still gated off
    // outside `?preview=2`, so the dashboard card keeps resolving to Agenda GPT
    // until they release — asserted in `useFeatureAccess.test.tsx`.
    items: [
      {
        id: "dash-searches",
        sectionId: "client-engagement",
        path: "/client-engagement/searches",
        label: "My Searches",
        icon: Search,
        phase: 2,
        // Released 2026-08-10 (delivery phase 2).
      },
      {
        id: "dash-detail",
        sectionId: "client-engagement",
        // The real Search Detail page lives at the `:searchId` sub-route
        // (registered in `routes.tsx`'s SUB_RESOURCE_ROUTES) — a search is
        // always opened FROM a row. This id-less path is the sidebar's
        // entry point and stays a placeholder.
        path: "/client-engagement/searches/detail",
        label: "Search Detail",
        icon: SearchCheck,
        phase: 2,
        // Released 2026-08-10 (delivery phase 2).
      },
      {
        id: "agenda-gpt",
        sectionId: "client-engagement",
        // In-app route: renders a native React form posting multipart/form-data
        // to the n8n AgendaGPT Form Trigger (see
        // `routes/client-engagement/AgendaForm.tsx`). Previously an
        // `externalUrl` shortcut to `nuse.app.n8n.cloud/form/…` (PR #153) —
        // replaced once we discovered n8n Cloud's Form Trigger blocks
        // cross-origin iframes (XFO + CSP) but accepts cross-origin POSTs.
        path: "/client-engagement/agenda",
        label: "AgendaGPT: Meeting Agenda Creator",
        navLabel: "Agenda GPT",
        icon: CalendarDays,
      },
    ],
  },
  {
    id: "closing",
    label: "Closing & Post-Close",
    navLabel: "Closing",
    icon: CheckCircle2,
    collapsible: true,
    items: [
      {
        id: "closing-email",
        sectionId: "closing",
        path: "/closing/email",
        label: "Send Search Closing Email",
        navLabel: "Closing Email",
        icon: Mail,
      },
      // NOTE: `closing-email-template` (Closing Email Template Editor) used to
      // be a peer child here. As of 2026-06-18 (Jared feedback) the editor is
      // demoted from a top-level sidebar destination to a secondary "Edit
      // templates" button INSIDE the "Send Search Closing Email" page — the
      // editor is an admin-side companion to the closing email flow, not a
      // standalone destination. The route + component + PAGE_OVERRIDES entry
      // are intentionally kept so the secondary button still navigates to
      // `/closing/email-template`.
    ],
  },
  {
    id: "dashboards",
    label: "Operations",
    icon: Settings,
    collapsible: true,
    items: [
      {
        id: "dash-company",
        sectionId: "dashboards",
        path: "/operations/company-metrics",
        label: "Company Metrics",
        icon: LayoutGrid,
      },
      // NOTE: `dash-searches` (My Searches) + `dash-detail` (Search Detail)
      // used to sit here. As of 2026-08-05 they live in the Client Engagement
      // section — see the comment on their new entries above.
      {
        id: "our-tech",
        sectionId: "dashboards",
        path: "/operations/technology",
        label: "Our Technology",
        icon: Share2,
      },
      {
        id: "sync-phone",
        sectionId: "dashboards",
        path: "/operations/sync-phone",
        label: "Sync Contacts to Phone",
        icon: Smartphone,
      },
      {
        id: "searches-import",
        sectionId: "dashboards",
        path: "/operations/searches-import",
        label: "Searches Import",
        icon: Download,
      },
      {
        id: "administration",
        sectionId: "dashboards",
        path: "/operations/administration",
        label: "Administration",
        icon: Shield,
        previewBadge: "nice-to-have",
        muted: true,
      },
    ],
  },
];

/** Today as a date-only ISO string (`YYYY-MM-DD`). Extracted so tests can
 * pin "now" via `vi.setSystemTime` and the comparison stays lexical. */
function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * `since` + `PILL_WINDOW_DAYS`, as the first date the window is OVER, or
 * `null` when `since` is not a parseable `YYYY-MM-DD` stamp.
 *
 * The null case is not theoretical bookkeeping: these constants are
 * hand-written, and `new Date("2026-8-17")`-style typos produce an Invalid
 * Date whose `toISOString()` THROWS. Since this runs inside the sidebar's
 * render, an unguarded throw would take the whole shell down over a
 * decoration. Failing to `null` (and from there to "not showing") keeps the
 * blast radius at the pill.
 */
function windowEnd(since: string): string | null {
  const end = new Date(`${since}T00:00:00Z`);
  if (Number.isNaN(end.getTime())) return null;
  end.setUTCDate(end.getUTCDate() + PILL_WINDOW_DAYS);
  return end.toISOString().slice(0, 10);
}

/**
 * Whether a dated announcement — a "new"/"updated" pill, or the Baby Chops
 * manual-install warning — should show today.
 *
 * True iff the window exists and today (date-only) falls in
 * `[since, since + PILL_WINDOW_DAYS)`: inclusive on the first day, exclusive
 * on the day the week is up. All stamps are `YYYY-MM-DD`, so both comparisons
 * are plain lexical string compares.
 *
 * TWO-sided, unlike the `until` gate it replaces. The lower bound is what
 * lets an announcement be merged before its day and switch itself on — under
 * the old rule a future-dated window showed immediately, which made "land it
 * Friday, announce it Monday" impossible to express.
 *
 * Independent of preview state — callers gate on `released` so a pill never
 * collides with the preview "coming soon" badge (which requires `!released`).
 * The day boundary is the UTC calendar day (`todayISODate` uses
 * `toISOString`), so for behind-UTC users an announcement can expire a few
 * hours early local-time (the safe direction) and appear a few hours early on
 * its first day.
 */
export function isAnnouncementActive(
  announcement: AnnouncementWindow | undefined,
  today: string = todayISODate(),
): boolean {
  if (!announcement) return false;
  const end = windowEnd(announcement.since);
  if (end === null) return false;
  return today >= announcement.since && today < end;
}

/**
 * Which announcement pill a page should show right now — `"new"`,
 * `"updated"`, or `null`.
 *
 * ONE definition of the rule, because it has two halves that are easy to
 * separate and were: an announcement shows only for a page the reader can
 * actually open — RELEASED, or (since 2026-09-02) revealed by the active
 * preview mode. Before this existed the rule lived in three places
 * (`WebLeftMenu` plus both install pages), two of which also hand-copied the
 * section id `"linkedin"` — a page moving sections would have silently
 * un-gated its own pill. The section id now comes from the model, where it
 * already lives.
 *
 * `revealed` (2026-09-02, operator directive — production-freeze review
 * flow): a page revealed by `?preview=N` with an ACTIVE announcement window
 * shows its pill instead of the "coming soon" balloon. Rationale: on the
 * frozen PROD build the dev deliveries are `released: false`, and the whole
 * point of previewing them is seeing what the next delivery announces — a
 * balloon says "not built"; these pages are built, just dark. The pill/
 * balloon mutual exclusivity survives with a new tiebreak: the pill WINS
 * while its window is active (`WebLeftMenu` suppresses the balloon when this
 * returns non-null); when the window lapses the balloon takes over again.
 * Callers with no preview context omit the option and keep the strict
 * released-only reading (the two page headers do — a pill on the page body
 * of an unreleased page would outlive its sidebar context). Options object
 * rather than positional params (iter-1 Agent A finding 3): the sidebar
 * only needs `revealed`, and `announcementFor(id, undefined, undefined,
 * true)` buried the one meaningful argument behind two placeholders.
 *
 * `flags` defaults to `activeFlags()` rather than `useFeatureAccess`, so this
 * is callable from a content-only page that renders outside the router in
 * tests. It resolves the same `released` value `useFeatureAccess().page()`
 * computes — both read the build-time registry — so the sidebar and the page
 * header cannot disagree.
 *
 * Tabs and other in-page features with no menu item resolve through
 * `IN_PAGE_ANNOUNCEMENTS` (2026-10-07), under the same rule.
 *
 * `newBadge` wins over `updatedBadge` when an item carries both, matching the
 * order `WebLeftMenu` resolved them in: one pill per row, never two stacked.
 */
export function announcementFor(
  pageId: string,
  {
    flags = activeFlags(),
    today = todayISODate(),
    revealed = false,
  }: { flags?: FeatureFlags; today?: string; revealed?: boolean } = {},
): "new" | "updated" | null {
  const item:
    | { sectionId: string; newBadge?: AnnouncementWindow; updatedBadge?: AnnouncementWindow }
    | undefined =
    LEFT_MENU_SECTIONS.flatMap((s) => s.items).find((i) => i.id === pageId) ??
    IN_PAGE_ANNOUNCEMENTS[pageId];
  if (!item) return null;
  if (!isPageReleased(flags, item.sectionId, pageId) && !revealed) return null;
  if (isAnnouncementActive(item.newBadge, today)) return "new";
  if (isAnnouncementActive(item.updatedBadge, today)) return "updated";
  return null;
}
